// Multi-day schedule helpers — block configuration, packing matches into
// blocks, formatting timestamps for display.
//
// Convention: scheduled_at strings are ISO 8601 with a Z suffix and represent
// WALL-CLOCK time (not true UTC). The Z is just an unambiguous parse marker;
// we read components via the get*UTC*() methods so a viewer in any timezone
// sees the same display. This keeps the data portable across operator
// laptops without timezone drift.
//
// Schedule config (config/schedule.json → ScheduleConfig):
//   - matchDurationMinutes: per-kind length (3v3 quals = 25, 1v1 = 15, etc.)
//   - days[]: each day has a date + 3 blocks (Morning/Afternoon/Evening),
//     local clock-time strings ("09:00" / "12:00"). Editable for special days.
//
// Pack function: takes ordered matches lacking timestamps + a starting block,
// and assigns each one to the next available slot. If a match wouldn't fit
// in the remaining time of the current block, advance to the next block.
// Used by Swiss-round generation; future delay-driven re-pack will reuse it.

import type { GroupLetter, Match, TeamConfig } from '../types';
import { GROUP_LETTERS } from '../types';

export interface ScheduleBlockConfig {
  id: string;
  label: string;
  /** Local clock time, "HH:MM". */
  startsLocal: string;
  /** Local clock time, "HH:MM". Block end is the latest a match may FINISH. */
  endsLocal: string;
}

export interface ScheduleDay {
  /** "YYYY-MM-DD". */
  date: string;
  blocks: ScheduleBlockConfig[];
}

export interface ScheduleConfig {
  /** Match length in minutes by kind. */
  matchDurationMinutes: Record<string, number>;
  days: ScheduleDay[];
}

/**
 * Block with absolute timestamps resolved against the day's date.
 * `starts` / `ends` are ISO 8601 wall-clock-as-UTC strings.
 */
export interface ResolvedBlock {
  id: string;
  label: string;
  date: string;
  /** ISO with Z; read components via UTC accessors. */
  starts: string;
  /** ISO with Z. */
  ends: string;
}

const PAD = (n: number) => n.toString().padStart(2, '0');

/** "HH:MM" + "YYYY-MM-DD" → "YYYY-MM-DDTHH:MM:00Z". */
function joinDateTime(date: string, hhmm: string): string {
  const [h, m] = hhmm.split(':');
  return `${date}T${h}:${m}:00Z`;
}

/**
 * Flatten config.days into one chronologically-ordered list of blocks with
 * absolute timestamps. Same order as config (assumed already chronological).
 */
export function resolveBlocks(config: ScheduleConfig): ResolvedBlock[] {
  const out: ResolvedBlock[] = [];
  for (const day of config.days) {
    for (const b of day.blocks) {
      out.push({
        id: b.id,
        label: b.label,
        date: day.date,
        starts: joinDateTime(day.date, b.startsLocal),
        ends:   joinDateTime(day.date, b.endsLocal),
      });
    }
  }
  return out;
}

/**
 * Find the block that contains the given scheduled_at, or null if none does.
 *
 * Compares via Date.parse rather than string-compare because PostgreSQL/
 * PostgREST serialize `timestamptz` as `+00:00`-suffixed ISO strings, while
 * our seeded blocks use the `Z` suffix. Lexicographically `+` < `Z`, so a
 * naive string compare falsely concludes a 09:00:00+00:00 match is BEFORE
 * the 09:00:00Z block start. Numeric epoch comparison is format-agnostic.
 */
export function findBlockFor(scheduledAt: string, blocks: ResolvedBlock[]): ResolvedBlock | null {
  const ms = Date.parse(scheduledAt);
  if (Number.isNaN(ms)) return null;
  for (const b of blocks) {
    if (ms >= Date.parse(b.starts) && ms < Date.parse(b.ends)) return b;
  }
  return null;
}

/**
 * Pack ordered matches into the configured blocks. Each match takes
 * `durationMinutesByKind[kind]` minutes; if it can't fit in the remaining
 * time of the current block, advance to the next block. Returns parallel
 * array of assigned ISO timestamps. If we run out of blocks before fitting
 * every match, the unfit matches get `null` (caller decides — overflow,
 * error, etc.).
 *
 * `from` (optional) — start packing at the first block whose starts >= from.
 *  Useful when ops manually pushes a match to "next block": pass the
 *  push-target block's starts to begin packing from there.
 *
 * Independent per-field: callers should pack 3v3 (north) and 1v1 (south)
 * separately so the two events don't compete for a single time slot.
 */
export function packMatchesIntoBlocks(
  items: { id: string; durationMin: number }[],
  blocks: ResolvedBlock[],
  options: { from?: string } = {},
): { assigned: Array<{ id: string; scheduledAt: string; blockId: string }>; overflow: string[] } {
  // Where to start: if `from` is given, find the block that contains it (or
  // the first block strictly after it if `from` is in a between-blocks gap).
  // Within that block, advance the cursor to max(block.starts, from) so we
  // resume packing where prior matches left off rather than at the block's
  // nominal start.
  const assigned: Array<{ id: string; scheduledAt: string; blockId: string }> = [];
  const overflow: string[] = [];
  let blockIdx = 0;
  let cursorMs: number | null = blocks[0] ? Date.parse(blocks[0].starts) : null;
  if (options.from) {
    const fromMs = Date.parse(options.from);
    let found = -1;
    for (let i = 0; i < blocks.length; i++) {
      const endMs = Date.parse(blocks[i].ends);
      if (fromMs < endMs) { found = i; break; }
      // `from` is past this block's end — keep looking.
    }
    if (found === -1) {
      cursorMs = null;
      blockIdx = blocks.length;
    } else {
      blockIdx = found;
      const startMs = Date.parse(blocks[blockIdx].starts);
      cursorMs = Math.max(startMs, fromMs);
    }
  }
  for (const item of items) {
    if (cursorMs == null) { overflow.push(item.id); continue; }
    let placed = false;
    while (blockIdx < blocks.length) {
      const block = blocks[blockIdx];
      const blockEndMs = Date.parse(block.ends);
      const matchEndMs = cursorMs + item.durationMin * 60_000;
      if (matchEndMs <= blockEndMs) {
        // Fits in current block.
        assigned.push({
          id: item.id,
          scheduledAt: isoUtcFromMs(cursorMs),
          blockId: block.id,
        });
        cursorMs = matchEndMs;
        placed = true;
        break;
      }
      // Doesn't fit — advance to next block.
      blockIdx += 1;
      cursorMs = blocks[blockIdx] ? Date.parse(blocks[blockIdx].starts) : null;
      if (cursorMs == null) break;
    }
    if (!placed) overflow.push(item.id);
  }
  return { assigned, overflow };
}

/**
 * Format an ISO scheduled_at as a friendly time string. Uses UTC accessors so
 * the stored wall-clock (Eastern) survives across viewer timezones, and tags
 * the result with "ET" so remote viewers know the zone — all event times are
 * Eastern regardless of where the page is loaded.
 */
export function formatScheduledAt(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = (h % 12) || 12;
  return `${h12}:${PAD(m)} ${period} ET`;
}

/** "2026-07-01T09:00:00Z" → "Jul 1". */
export function formatScheduledDay(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
  return `${month} ${d.getUTCDate()}`;
}

/** "2026-07-01T09:00:00Z" → "2026-07-01". Stable date key for grouping. */
export function dayKey(iso: string | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/** Long form: "Jul 1 · 9:00 AM". Used in tooltips / multi-day match lists. */
export function formatScheduledLong(iso: string | undefined): string {
  if (!iso) return '';
  return `${formatScheduledDay(iso)} · ${formatScheduledAt(iso)}`;
}

function isoUtcFromMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${PAD(d.getUTCMonth() + 1)}-${PAD(d.getUTCDate())}` +
         `T${PAD(d.getUTCHours())}:${PAD(d.getUTCMinutes())}:${PAD(d.getUTCSeconds())}Z`;
}

// =====================================================================
//  Block-aware delay helpers (Schedule L2)
// =====================================================================
// A "current block" is whichever block contains the live match for an
// event. If no live, fall back to on-deck; if neither, the next non-
// completed match. Used to scope delay application: matches in the
// current block see the delay; matches in later blocks display nominal
// time (the assumption being block boundaries — lunch / dinner — absorb
// reasonable delay without affecting downstream blocks).

interface MatchLike {
  id?: string;
  scheduledAt?: string;
  redS?: string;
  blueS?: string;
  live?: boolean;
  onDeck?: boolean;
}

const COMPLETED_LIKE_STATUSES = new Set(['completed', 'forfeit_v', 'forfeit_iv']);

/**
 * Find the block containing the "current" match for an event:
 *   1. live match if any
 *   2. on-deck match
 *   3. earliest match that has not concluded
 * Returns null if no candidate exists or its scheduledAt isn't in any block.
 */
export function currentBlockFor<T extends MatchLike>(
  matches: T[],
  blocks: ResolvedBlock[],
): ResolvedBlock | null {
  const live = matches.find((m) => m.live);
  const onDeck = matches.find((m) => m.onDeck);
  const upcoming = matches
    .filter((m) =>
      !COMPLETED_LIKE_STATUSES.has(m.redS ?? '') ||
      !COMPLETED_LIKE_STATUSES.has(m.blueS ?? ''),
    )
    .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''))[0];
  const candidate = live ?? onDeck ?? upcoming;
  if (!candidate?.scheduledAt) return null;
  return findBlockFor(candidate.scheduledAt, blocks);
}

/**
 * Effective scheduled-at for a match, accounting for delay. Delay applies
 * only when the match's nominal scheduledAt falls within `currentBlock`;
 * otherwise the nominal time is returned (matches in later blocks run on
 * their original schedule, since the inter-block break absorbed the delay).
 *
 * If the match has no scheduledAt, returns undefined unchanged.
 */
export function effectiveScheduledAt(
  match: MatchLike,
  currentBlock: ResolvedBlock | null,
  delayMinutes: number,
): string | undefined {
  if (!match.scheduledAt) return undefined;
  if (!currentBlock || delayMinutes <= 0) return match.scheduledAt;
  // Only apply delay if the match's nominal time is inside the current block.
  // Numeric compare so `+00:00` and `Z` ISO suffixes both work — see findBlockFor.
  const ms = Date.parse(match.scheduledAt);
  const startMs = Date.parse(currentBlock.starts);
  const endMs = Date.parse(currentBlock.ends);
  if (ms < startMs || ms >= endMs) return match.scheduledAt;
  return isoUtcFromMs(ms + delayMinutes * 60_000);
}

/**
 * True when the match's effective start (nominal + applied delay) lands
 * outside any active block, OR lands inside a block but its end runs past
 * that block's end. Either way, ops should consider rescheduling.
 *
 * Cases this catches:
 *   1. Delay pushes a current-block match past the block's end (the
 *      classic case — match was 11:55 in a morning block ending at
 *      12:00; with 30 min delay it now ends at 12:25, overflows).
 *   2. Match was reschedule-set to a time inside a break (e.g. 12:20 PM
 *      during the noon lunch gap). Without this case, the old logic
 *      returned false because the nominal time was past the morning
 *      block's end and the function assumed "in a later block, not my
 *      problem". Breaks aren't blocks — anything in one is overflow.
 *   3. Match scheduled past all blocks of the day. Same as (2): the
 *      effective time has no block to live in, so it's overflow.
 *
 * Matches that fit cleanly in a non-current block (e.g. 1 PM afternoon
 * match while the current block is morning) still return false — those
 * are healthy schedule rows, just not "now".
 */
export function isOverflowing(
  match: MatchLike,
  blocks: ResolvedBlock[],
  currentBlock: ResolvedBlock | null,
  delayMinutes: number,
  durationMin: number,
): boolean {
  if (!match.scheduledAt) return false;
  // Playoff matches (ids U_*/E_*) run at fixed PLAYOFF_SCHEDULE times and are never
  // block-packed, so the "won't fit in its block" overflow concept doesn't apply —
  // they never show OVERTIME. (The playoff days now DO have a block so the official
  // delay can anchor there during elims; overflow stays exempt for elims regardless.)
  if (match.id?.startsWith('U_') || match.id?.startsWith('E_')) return false;
  // Effective time = nominal + delay (delay applies only when the match's
  // nominal time is within currentBlock; everywhere else it's the nominal).
  const effective = effectiveScheduledAt(match, currentBlock, delayMinutes);
  if (!effective) return false;
  const effectiveMs = Date.parse(effective);
  // If the effective time isn't inside any block, the match is sitting in
  // a break (or past EOD). That's a scheduling problem — flag it.
  const effectiveBlock = findBlockFor(effective, blocks);
  if (!effectiveBlock) return true;
  // Otherwise the match lives in some block. Overflow only if its end
  // runs past that block's end.
  return effectiveMs + durationMin * 60_000 > Date.parse(effectiveBlock.ends);
}

/**
 * Find the block immediately after the given one, or null at end-of-day-N.
 */
export function nextBlockAfter(block: ResolvedBlock, blocks: ResolvedBlock[]): ResolvedBlock | null {
  const idx = blocks.findIndex((b) => b.id === block.id);
  if (idx === -1) return null;
  return blocks[idx + 1] ?? null;
}

/**
 * Information about a same-day inter-block gap (lunch / dinner) crossed
 * by the prev → next match pair. Used to render break separators in
 * schedule views. Returns null when no gap is crossed.
 *
 * Block assignment for matches scheduled INSIDE a gap uses the gap's
 * midpoint as the dividing line: a match in the first half of the lunch
 * window is treated as a morning match (no separator before it), and a
 * match in the second half is treated as an afternoon match (separator
 * appears before it). This matches the operator's mental model — a
 * 12:10 PM match feels like an extended-morning match, while a 1:45 PM
 * match feels like an early-afternoon match.
 *
 * Implementation: walks each pair of chronologically adjacent same-day
 * blocks and checks whether prev sits before the gap's midpoint while
 * next sits at-or-after it. Naturally handles:
 *   - the canonical case (prev in block A, next in block B)
 *   - matches scheduled DURING the gap (assigned per midpoint)
 *   - back-to-back same-block matches (no break)
 *   - cross-day pairs (handled by the day marker in the UI; we skip)
 *
 * Label heuristic: time-of-day based.
 *   12:00–13:59 start → "Lunch"
 *   17:00–18:59 start → "Dinner"
 *   anything else     → "Break"
 */
export interface BreakInfo {
  starts: string;
  ends: string;
  durationMinutes: number;
  label: 'Lunch' | 'Dinner' | 'Break';
}
export function breakBetween(
  prev: MatchLike | null | undefined,
  next: MatchLike | null | undefined,
  blocks: ResolvedBlock[],
): BreakInfo | null {
  if (!prev?.scheduledAt || !next?.scheduledAt) return null;
  const prevMs = Date.parse(prev.scheduledAt);
  const nextMs = Date.parse(next.scheduledAt);
  if (Number.isNaN(prevMs) || Number.isNaN(nextMs)) return null;
  // Out-of-order pairs would create spurious breaks; ignore.
  if (nextMs <= prevMs) return null;

  for (let i = 0; i < blocks.length - 1; i++) {
    const a = blocks[i];
    const b = blocks[i + 1];
    if (a.date !== b.date) continue;        // cross-day → day marker handles it
    const gapStartMs = Date.parse(a.ends);
    const gapEndMs = Date.parse(b.starts);
    if (gapEndMs <= gapStartMs) continue;   // no actual gap (back-to-back blocks)
    // Midpoint of the gap is the dividing line. prev "morning-side", next
    // "afternoon-side" → break separator goes between them.
    const midMs = gapStartMs + Math.floor((gapEndMs - gapStartMs) / 2);
    if (prevMs < midMs && nextMs >= midMs) {
      const startHour = new Date(a.ends).getUTCHours();
      const label: BreakInfo['label'] =
        (startHour >= 12 && startHour < 14) ? 'Lunch' :
        (startHour >= 17 && startHour < 19) ? 'Dinner' :
        'Break';
      return {
        starts: a.ends,
        ends: b.starts,
        durationMinutes: Math.round((gapEndMs - gapStartMs) / 60_000),
        label,
      };
    }
  }
  return null;
}

/**
 * Resolve the displayed time for a match given delay state. Returns the
 * effective time (delay-shifted if in the current block, nominal otherwise)
 * AND a `delayed` flag so the caller can render a visual cue.
 *
 * Treats the delay as inactive (= 0) when its anchor block doesn't match
 * the current block — that's how block-boundary "delay reset" surfaces in
 * the UI. Once ops fires another ADD_DELAY in the new block, the anchor
 * updates and the new delay applies.
 */
export interface DisplayTime {
  effective: string | undefined;
  delayed: boolean;
  /** Minutes of delay applied (0 when delay is inactive or out-of-block). */
  appliedDelay: number;
}
export function displayTimeFor<T extends MatchLike>(
  match: T,
  matchesInEvent: T[],
  blocks: ResolvedBlock[],
  delayMinutes: number,
  delayAnchorBlockId: string | null,
): DisplayTime {
  const cb = currentBlockFor(matchesInEvent, blocks);
  // Stale anchor (block changed) → delay treated as 0.
  const delayActive = !!cb && cb.id === delayAnchorBlockId && delayMinutes > 0;
  const applied = delayActive ? delayMinutes : 0;
  const effective = effectiveScheduledAt(match, cb, applied);
  // Did the delay actually shift this match? (Outside the current block →
  // effective === nominal even when delay is active.)
  const delayed = delayActive && effective !== match.scheduledAt;
  return { effective, delayed, appliedDelay: applied };
}

// Default ARC-2026 schedule used as a fallback when config/schedule.json
// isn't available at runtime (tests, mock seed). Mirrors the published draft:
// 3 days, 3 blocks per day, lunch 12-2, dinner 5-7 (afternoon ends 17:00 so
// dinner starts an hour earlier; the evening block stays put). Evening blocks run 19:00–
// 20:15 (75 min) — exactly 3 × 25-min 3v3 matches (last ends 20:15) or 5 ×
// 15-min 1v1 matches (last ends 20:15). The earlier 21:10 end was trimmed back
// once we confirmed the schedule has enough buffer budgeted elsewhere.
export const DEFAULT_ARC_SCHEDULE: ScheduleConfig = {
  matchDurationMinutes: { qual_3v3: 25, qual_1v1: 15, playoff_3v3: 30, playoff_1v1: 20 },
  days: [
    {
      date: '2026-07-01',
      blocks: [
        { id: 'D1-M', label: 'Day 1 · Morning',   startsLocal: '09:00', endsLocal: '12:00' },
        { id: 'D1-A', label: 'Day 1 · Afternoon', startsLocal: '14:00', endsLocal: '17:00' },
        { id: 'D1-E', label: 'Day 1 · Evening',   startsLocal: '19:00', endsLocal: '20:15' },
      ],
    },
    {
      date: '2026-07-02',
      blocks: [
        { id: 'D2-M', label: 'Day 2 · Morning',   startsLocal: '09:00', endsLocal: '12:00' },
        { id: 'D2-A', label: 'Day 2 · Afternoon', startsLocal: '14:00', endsLocal: '17:00' },
        { id: 'D2-E', label: 'Day 2 · Evening',   startsLocal: '19:00', endsLocal: '20:15' },
      ],
    },
    {
      date: '2026-07-03',
      blocks: [
        { id: 'D3-M', label: 'Day 3 · Morning',   startsLocal: '09:00', endsLocal: '12:00' },
        { id: 'D3-A', label: 'Day 3 · Afternoon', startsLocal: '14:00', endsLocal: '17:00' },
        { id: 'D3-E', label: 'Day 3 · Evening',   startsLocal: '19:00', endsLocal: '20:15' },
      ],
    },
    // Playoff days. Elims run on a bespoke per-slot timeline (PLAYOFF_SCHEDULE),
    // NOT packed into these blocks — but the block-aware official-delay logic
    // needs the current match to land in SOME block to anchor to, else a delay
    // never applies during elims. One wide block per playoff day covers every
    // bracket slot (3v3 Jul 4 → 20:00 + Jul 5 finals; 1v1 all Jul 4 → 16:20), so
    // a delay shifts the remaining same-day matches; ops clears it once a
    // between-round gap has caught the schedule back up.
    {
      date: '2026-07-04',
      blocks: [
        { id: 'D4-PO', label: 'Day 4 · Playoffs', startsLocal: '09:00', endsLocal: '20:15' },
      ],
    },
    {
      date: '2026-07-05',
      blocks: [
        { id: 'D5-PO', label: 'Day 5 · Finals', startsLocal: '09:00', endsLocal: '12:00' },
      ],
    },
  ],
};

// Playoff (elimination) schedule. Unlike quals — which are packed into the block
// grid — elims run on a bespoke, per-event timeline on Jul 4-5, so each of the
// 15 bracket slots has an explicit start time. 3v3 = 30-min slots, 1v1 = 20-min
// slots; both run in parallel on their own field. Times are wall-clock (stored
// as UTC, displayed verbatim via the UTC formatters). GFR (grand-final reset)
// always gets a reserved slot but is only played if the lower-bracket team wins
// the grand final. The Bracket screen reads these for the projected times shown
// before the bracket is generated; generate_bracket inserts the matches with
// the same values (passed as p_times).
//
// Play order is set by the scheduled_at times here, NOT by PLAYOFF_SLOT_ORDER
// (that array only governs the p_times ↔ generate_bracket VALUES alignment). The
// public match-code numbering (src/lib/match-code.ts) derives from these times,
// so it follows play order automatically.
//
// 3v3 runs lower-bracket-before-upper wherever the bracket allows: LB5 (lower
// semi) is scheduled BEFORE UB7 (upper final). 1v1 deliberately does the opposite
// at the top — UB7 (upper final) plays BEFORE LB5 (lower semi) — and swaps the
// LB3/LB4 (lower R2) slot times so upper-bracket drops get more rest. So LB5 < UB7
// holds for 3v3 but NOT 1v1; see the schedule tests.
export const PLAYOFF_SCHEDULE: Record<'3v3' | '1v1', Record<string, string>> = {
  '3v3': {
    UB1: '2026-07-04T09:00:00Z', UB2: '2026-07-04T09:30:00Z', UB3: '2026-07-04T10:00:00Z', UB4: '2026-07-04T10:30:00Z',
    LB1: '2026-07-04T13:00:00Z', LB2: '2026-07-04T13:30:00Z', UB5: '2026-07-04T14:00:00Z', UB6: '2026-07-04T14:30:00Z',
    LB3: '2026-07-04T16:00:00Z', LB4: '2026-07-04T16:30:00Z', LB5: '2026-07-04T18:00:00Z',
    UB7: '2026-07-04T19:30:00Z',
    LB6: '2026-07-05T09:00:00Z', GF:  '2026-07-05T10:00:00Z', GFR: '2026-07-05T10:30:00Z',
  },
  '1v1': {
    UB1: '2026-07-04T09:00:00Z', UB2: '2026-07-04T09:20:00Z', UB3: '2026-07-04T09:40:00Z', UB4: '2026-07-04T10:00:00Z',
    LB1: '2026-07-04T10:40:00Z', LB2: '2026-07-04T11:00:00Z', UB5: '2026-07-04T11:20:00Z', UB6: '2026-07-04T11:40:00Z',
    // LB3/LB4 (lower R2) swapped in time so the upper-bracket drops get more rest.
    LB4: '2026-07-04T12:20:00Z', LB3: '2026-07-04T12:40:00Z',
    // 1v1 runs the upper final (UB7) BEFORE the lower semi (LB5) — opposite of 3v3.
    UB7: '2026-07-04T13:00:00Z', LB5: '2026-07-04T13:40:00Z',
    LB6: '2026-07-04T14:40:00Z', GF:  '2026-07-04T15:40:00Z', GFR: '2026-07-04T16:00:00Z',
  },
};

// Bracket-slot order used by generate_bracket's INSERT (the order p_times must
// follow). Matches the VALUES order in the migration: UB1-4, LB1-2, UB5-6,
// LB3-4, UB7, LB5, LB6, GF, GFR.
export const PLAYOFF_SLOT_ORDER = [
  'UB1', 'UB2', 'UB3', 'UB4', 'LB1', 'LB2', 'UB5', 'UB6', 'LB3', 'LB4', 'UB7', 'LB5', 'LB6', 'GF', 'GFR',
] as const;

/** The 15 elim start times for an event, in PLAYOFF_SLOT_ORDER — passed to
 *  generate_bracket as p_times so the inserted matches match the projection. */
export function playoffMatchTimes(event: '3v3' | '1v1'): string[] {
  return PLAYOFF_SLOT_ORDER.map((slot) => PLAYOFF_SCHEDULE[event][slot]);
}

// ── 3v3 group filtering ──────────────────────────────────────────────────────
// The 3v3 schedule can be narrowed to a single round-robin group. A match's
// group is well-defined ONLY for an intra-group pairing: group-stage quals pair
// two teams from the SAME group, so we read their shared group letter (a bye —
// blue empty — takes the byed team's group). Playoff bracket rows pair teams
// from DIFFERENT groups → no single group → undefined, so they only ever show
// under the "All" filter. Ungrouped teams (pre-Captain's-Meeting) → undefined.
export function groupOfMatch(m: Pick<Match, 'red' | 'blue'>, teamConfig: TeamConfig): GroupLetter | undefined {
  const red = teamConfig[m.red]?.group;
  if (!red) return undefined;
  const blue = m.blue ? teamConfig[m.blue]?.group : red;
  return blue === red ? red : undefined;
}

// Distinct 3v3 groups present across `matches`, in canonical A–D order — drives
// which group chips the schedule filter offers (and whether to show it at all).
export function groupsInMatches(matches: readonly Match[], teamConfig: TeamConfig): GroupLetter[] {
  return GROUP_LETTERS.filter((g) => matches.some((m) => groupOfMatch(m, teamConfig) === g));
}
